import { useCallback, useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { studyName, studyPath, useCollection } from '../api/collections';
import { usePublicCollection } from '../api/explore';
import { fetchStudyHead, studyHeadKey } from '../api/studyHead';
import { IconChevronLeft, IconChevronRight } from '../ui/icons';
import type { StudyHead } from './StudyPageCommunity';
import { inPageOrder, neighboursOf, type Placed } from './studyNeighbours';

/**
 * The studies around the one on screen, for the viewer's previous/next: the
 * collection's studies in the order its page lists them, where each one's
 * viewer address is, and how to have a neighbour's head ready before it is
 * asked for.
 */
export interface Siblings {
  order: Placed[];
  hrefOf: (studyId: string) => string;
  prefetch?: (studyId: string) => void;
  /** The page reads a bounded slice of a collection: true when it stopped before the collection's end. */
  truncated?: boolean;
}

/**
 * The siblings of a study opened from the app's own routes. The collection is
 * read the way the person saw it: a member of the study's workspace reads the
 * member page (every study, the unpublished ones too); anybody else the public
 * page, which lists only studies with a published image. Both are the queries
 * the collection pages keep, so coming from one of them costs no request.
 * Nothing for a study that stands outside any collection.
 */
export function useStudySiblings(head: StudyHead | null | undefined): Siblings | undefined {
  const qc = useQueryClient();
  const id = head?.collection_id ?? undefined;
  const member = !!head?.workspace_id;
  const own = useCollection(member ? id : undefined);
  const pub = usePublicCollection(false, member ? undefined : id);
  const c = member ? own.data : pub.data;
  const order = useMemo(() => (c ? inPageOrder(c.groups, c.studies) : undefined), [c]);
  // The member page says how many it holds in all; the public one reads whole.
  const truncated = member && own.data ? own.data.studies.length < own.data.total : false;
  const prefetch = useCallback(
    (studyId: string) => void qc.prefetchQuery({ queryKey: studyHeadKey(studyId), queryFn: () => fetchStudyHead<StudyHead>(studyId) }),
    [qc],
  );
  return useMemo(
    () => (order ? { order, hrefOf: (studyId: string) => studyPath(studyId), prefetch, truncated } : undefined),
    [order, prefetch, truncated],
  );
}

/** A quiet button (user, 2026-09-30): nothing at rest, a filled pill under the cursor. */
const STEP: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, height: 26, borderRadius: 6, color: 'var(--tr-ink-2)', textDecoration: 'none', fontSize: 12.5, fontWeight: 500, whiteSpace: 'nowrap', flex: 'none' };
/** Each says what it does beside its chevron (user, 2026-09-30). */
const PREV: React.CSSProperties = { ...STEP, padding: '0 9px 0 4px' };
const NEXT: React.CSSProperties = { ...STEP, padding: '0 4px 0 9px' };

/**
 * Previous / next study, after the study's name in the viewer's top bar:
 * quiet "‹ Previous" and "Next ›" buttons, stepping through the collection's
 * studies in the order its page lists them. Each names the study it goes to
 * in its tooltip; at either end it says so and does nothing, rather than wrap
 * around — and where the list was cut short (a collection past what one page
 * reads) the end says that instead of claiming to be the last. Both
 * neighbours' heads are fetched ahead, so the step is instant. Not drawn at
 * all, not even its row, when the study is not on the collection's page
 * (outside any collection, or past what the page reads). On a phone the pair
 * moves to a row of its own under the bar, one button at each edge
 * (.tr-viewer-pager, community.css).
 */
export function StudyNav({ order, hrefOf, prefetch, truncated, current }: Siblings & { current: string }) {
  const n = useMemo(() => neighboursOf(order, current), [order, current]);
  useEffect(() => {
    if (!n || !prefetch) return;
    if (n.prev) prefetch(n.prev.public_id);
    if (n.next) prefetch(n.next.public_id);
  }, [n, prefetch]);
  if (!n) return null;
  const step = (dir: 'prev' | 'next', to: Placed | undefined) => {
    const label = dir === 'prev' ? 'Previous study' : 'Next study';
    const body = dir === 'prev' ? <><IconChevronLeft size={15} sw={2.2} /><span>Previous</span></> : <><span>Next</span><IconChevronRight size={15} sw={2.2} /></>;
    const style = dir === 'prev' ? PREV : NEXT;
    const atEnd = dir === 'prev' ? 'First study in the collection' : truncated ? `Only the first ${order.length} studies of the collection are listed here` : 'Last study in the collection';
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          {to ? (
            <Link to={hrefOf(to.public_id)} aria-label={`${label}: ${studyName(to)}`} className="tr-hover-track-ink" style={style}>
              {body}
            </Link>
          ) : (
            <span role="button" aria-disabled="true" aria-label={label} style={{ ...style, opacity: 0.3, cursor: 'default' }}>
              {body}
            </span>
          )}
        </TooltipTrigger>
        <TooltipContent side="bottom">{to ? `${label} · ${studyName(to)}` : atEnd}</TooltipContent>
      </Tooltip>
    );
  };
  return (
    <div role="group" aria-label="Studies in this collection" className="tr-viewer-pager" style={{ display: 'flex', alignItems: 'center', gap: 2, marginLeft: 6, flex: 'none' }}>
      {step('prev', n.prev)}
      {step('next', n.next)}
    </div>
  );
}
